//-- Copyright (c) 2021 TMT Insights Group, LLC
import UserDisplay from '@/components/_common/AssigneeSelect/UserDisplay';
import PolarisIcon from '@/components/_common/PolarisIcon';
import Ticker from '@/components/_common/Ticker';
import { ConditionalTooltip } from '@/exports/columnCommonComponents';
import { applyFilter, clearFilters, setCurrentTable, setParams } from '@/redux/slices/dataTableSlice';
import { OrderJobTask } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { TimelineContent } from '@mui/lab';
import { Box, Button, Typography } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import StatusReasonPopover from '../../_common/Tasks/StatusReasonPopover';
import { useAppDispatch } from '@/redux/hooks';
import { releaseJobEmbargoDate } from '@/redux/slices/jobSlice';
import useConfirmationPrompt from '@/components/_hooks/useConfirmationPrompt';
import { useToast } from '@/components/_common/Toast';

export interface JobMilestoneStatusProps {
  status: string;
  prettyName: string;
  task?: OrderJobTask;
  showTicker: boolean;
  tickerStartFrom?: string;
  isFinalCompleted?: boolean;
  isFirstPending: boolean;
  multipleTasks?: OrderJobTask[] | undefined; // TODO: needed for POC quickly, will be removed
  isOnHold: boolean;
}

const JobMilestoneStatus = ({
  status,
  prettyName,
  task,
  showTicker = false,
  isFinalCompleted = false,
  isFirstPending = false,
  tickerStartFrom,
  multipleTasks,
  isOnHold = false
}: JobMilestoneStatusProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { success, error } = useToast();
  const isFailed = status === 'failed';
  const isAssigned = !!task?.assigned_user;
  const isResolved = task?.status?.includes('resolved');
  const isSkipped = task?.status === 'skipped';
  const params = useParams() as any;
  const jobId = params.id;

  const handleReleaseSubmit = () => {
    dispatch(releaseJobEmbargoDate({ id: jobId as number }))
      .unwrap()
      .then(() => {
        closeLoadingDialog();
        success('Successfully released embargo date');
      })
      .catch((e) => {
        closeLoadingDialog();
        console.error('Failed to release embargo date', e);
        error(
          <div>
            <div>Embargo release failed.</div>
            <div>Retry in a few minutes or contact support if the issue persists</div>
          </div>
        );
      });
  };
  const {
    dialog: confirmationPrompt,
    setDialogVisible,
    closeLoadingDialog
  } = useConfirmationPrompt({
    title: 'Release Embargo Date',
    message: 'Are you sure you want to release embargo date?',
    buttonColor: 'error',
    buttons: {
      submitText: 'Release Embargo',
      cancelText: 'No'
    },
    onConfirm: handleReleaseSubmit
  });

  const getStatusColor = () => {
    if (task && (task?.status === 'cancelled' || task?.status === 'cancelling')) return 'cancelled';
    else if (isAssigned) return 'primary';
    else return 'default';
  };

  const renderStatus = () => {
    if (isFinalCompleted)
      return (
        <Box sx={{ pr: 1 }}>
          <StatusComplete>{prettyName}</StatusComplete>
        </Box>
      );

    switch (status) {
      case 'active':
        if (task?.status === 'cancelled' || task?.status === 'cancelling') return <StatusCancelled>{prettyName}</StatusCancelled>;
        return <StatusActive color={getStatusColor()}>{prettyName}</StatusActive>;
      case 'complete':
      case 'accepted':
        return <StatusComplete>{prettyName}</StatusComplete>;
      case 'failed':
        if (isFirstPending) return <StatusFailed>{prettyName}</StatusFailed>;
        else return <StatusComplete>{prettyName}</StatusComplete>;
      case 'hold':
        return <StatusHold>{prettyName}</StatusHold>;
      case 'rejected':
        return <StatusFailed>{prettyName}</StatusFailed>;
      default:
        return <StatusFuture>{prettyName}</StatusFuture>;
    }
  };

  const renderTaskText = () => {
    if (isResolved) {
      return 'Task Resolved';
    } else if (isFailed) {
      return 'Task Failed';
    } else if (isSkipped) {
      return 'Task Skipped';
    } else if (task?.status === 'rejected') {
      return 'Task Rejected';
    } else if (task?.status === 'cancelled' || task?.status === 'cancelling' || status !== 'active' || isAssigned) {
      if (multipleTasks && multipleTasks.length > 1) return 'View Tasks';
      // if the task is NOT active, or if it IS assigned to someone
      return 'View Task';
    } else {
      return 'Task Waiting';
    }
  };

  const getTaskColor = () => {
    if (isFailed && isFirstPending) return 'warning';
    if (task?.status === 'cancelled' || task?.status === 'cancelling' || status !== 'active') return 'default';
    if (isAssigned) return 'info';
    else return 'warning';
  };

  const handleTaskClick = () => {
    // we need the HID to properly filter the table, so if it's missing, do nothing
    if (!task?.hid || !task?.id) {
      return null;
    }
    // if status is failed or there is a status reason, this is a processing exception
    // and all others are tasks
    if (isFailed) {
      dispatch(setCurrentTable('processingTasks'));
      dispatch(clearFilters(undefined));
      dispatch(applyFilter({ type: 'filter_id', selections: [task?.hid || task?.id, 'task_hid_filter'] }));
      if (task?.status_reason) dispatch(setParams({ status_filter: ['in_error', 'resolved', 'failed'] }));
      history.push(`/processing-exceptions`);
    } else {
      dispatch(setCurrentTable('tasks'));
      dispatch(clearFilters(undefined));
      if (multipleTasks && multipleTasks.length) {
        dispatch(applyFilter({ type: 'search', selections: multipleTasks.map((t) => t.hid).join(',') }));
      } else {
        dispatch(applyFilter({ type: 'filter_id', selections: [task.hid, 'task_hid_filter'] }));
      }
      // we need the show_not_ready filter applied if this is a future task
      if (task.status === 'not_ready') dispatch(applyFilter({ type: 'show_not_ready', selections: true }));
      history.push(`/tasks/?id=${task.id}`);
    }
  };

  const handlePendingAssetClick = () => {
    if (!jobId) {
      return null;
    }
    dispatch(setCurrentTable('pendingAssets'));
    dispatch(clearFilters(undefined));
    dispatch(applyFilter({ type: 'filter_id', selections: [`J${jobId}`, 'job_hid_filter'] }));
    history.push(`/pending-assets/`);
  };

  const taskColor = getTaskColor();

  const renderTaskButton = () => {
    if (status === 'rejected') {
      return (
        <div>
          <RejectedTaskButton data-testid="rejected-task-click" color={'error'} onClick={handleTaskClick}>
            {/* if there is no status_reason, this is a resolved task */}
            <PolarisIcon height={15} icon={'cancel'} style={{ marginRight: 3 }} />
            {renderTaskText()}
          </RejectedTaskButton>
        </div>
      );
    } else if (isFailed || task?.is_manual_task) {
      return (
        <div>
          <ConditionalTooltip
            show={!!task?.is_manual_task}
            arrow
            leaveDelay={200}
            title={<UserDisplay user={task?.assigned_user || null} canAssign={false} />}
            placement="bottom"
          >
            <TaskButton data-testid="failed-task-click" variant="text" color={taskColor} onClick={handleTaskClick}>
              {/* if there is no status_reason, this is a resolved task */}
              <PolarisIcon height={isFailed ? 16 : 18} icon={isSkipped ? 'skip' : isFailed ? 'error' : 'waving'} style={{ marginRight: 3 }} />
              {renderTaskText()}
            </TaskButton>
          </ConditionalTooltip>
        </div>
      );
    } else if (isSkipped) {
      return <Typography sx={{ fontStyle: 'italic', opacity: 0.5, fontSize: '.9rem' }}>Skipped</Typography>;
    }
  };

  return (
    // do not add whitespace:nowrap here, it breaks the layout
    <TimelineContent sx={{ mt: isFinalCompleted ? 1.5 : 0 }}>
      {/* the status text */}
      {renderStatus()}

      {/* the ticker */}
      {showTicker && task?.status !== 'accepted' && task?.status !== 'cancelled' && task?.status !== 'cancelling' && status !== 'rejected' && (
        <Ticker fromTime={tickerStartFrom} />
      )}

      {isOnHold && (
        <>
          <Typography variant="caption">On Hold</Typography>
          <div>
            <TaskButton variant="outlined" color="warning" onClick={() => setDialogVisible(true)} sx={{ opacity: 0.7 }}>
              {/* if there is no status_reason, this is a resolved task */}
              Release
              <PolarisIcon height={16} icon="cancel" style={{ marginLeft: 3 }} />
            </TaskButton>
          </div>
          {confirmationPrompt}
        </>
      )}

      {/* the pending asset button */}
      {task?.is_assets_task && (
        <div>
          <TaskButton
            variant="text"
            color={status !== 'active' || task?.status === 'cancelled' || task?.status === 'cancelling' ? 'default' : 'secondary'}
            onClick={handlePendingAssetClick}
          >
            <PolarisIcon icon="pending" height={20} style={{ marginRight: 3 }} />
            View Asset(s)
          </TaskButton>
        </div>
      )}

      {/* the task button */}
      {renderTaskButton()}

      {/* status reason popup */}
      {!!task?.status_reason && <StatusReasonPopover statusReason={task.status_reason} onTaskClick={handleTaskClick} isFailed={isFailed} />}
    </TimelineContent>
  );
};

export default JobMilestoneStatus;

const TaskButton = styled(Button)`
  margin-right: ${({ theme }) => theme.spacing(-1)};
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
  margin-bottom: 0;
  font-size: 0.9rem;
  line-height: 1;
`;

const RejectedTaskButton = styled(Button)`
  margin-right: ${({ theme }) => theme.spacing(-1)};
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
  margin-bottom: 0;
  font-size: 0.9rem;
  line-height: 1;
  background: ${({ theme }) => theme.palette.error.main + '2a'};
`;

const Status = styled.div`
  font-size: 1.1rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
`;

const StatusActive = styled(Status)<{ color: string }>`
  color: ${({ theme, color }) => {
    if (color === 'primary') return theme.palette.info.main;
    else if (color === 'cancelled') return theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[700];
    else return theme.palette.warning.main;
  }};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  margin-top: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

const StatusComplete = styled(Status)``;
const StatusCancelled = styled(Status)`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  opacity: 0.5;
`;

const StatusFailed = styled(Status)`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  color: ${({ theme }) => theme.palette.error.main};
`;

const StatusHold = styled(Status)`
  color: ${({ theme }) => theme.palette.warning.main};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const StatusFuture = styled(Status)`
  opacity: 0.5;
`;
